// VEIL-Neb データモデル（外枠）。Firestore コレクションのスキーマ定義。
// 実データのバインド・移行は TMS 側（中身）で実施。共通参照としてここを正とする。
// 出所: 外枠 要件定義書 v1.3（6章 データモデル / 7章 計算ロジック）。

export const COLLECTIONS = {
  trips: 'trips',            // 運行（日計）
  orders: 'orders',          // 配車オーダー
  customers: 'customers',    // 得意先
  partners: 'partners',      // 協力会社
  drivers: 'drivers',        // 乗務員
  vehicles: 'vehicles',      // 車両
  tariffs: 'tariffs',        // 運賃タリフ
  tachographs: 'tachographs',// デジタコ実績（車番×日）
  invoices: 'invoices',      // 請求
  users: 'users',            // 利用者・ロール
  settings: 'settings',      // アプリ設定（給与反映など）
  teikibin: 'teikibin',      // 定期便マスタ
  imports: 'imports',        // 取込ラン記録（中身/TMS）: いつ・どのCSVを・何件取り込んだか
  tripChanges: 'tripChanges',// 運行の内容変更履歴（中身/TMS）: ②変更検知の差分（高速代など）
  chargeSheets: 'chargeSheets',// 運賃お伺い書 発行履歴（中身/TMS）: 得意先(請求)/協力会社(支払)
  chargeSheetLines: 'chargeSheetLines', // お伺い明細（物理テーブルは charge_sheet_lines）
  requests: 'requests',        // 依頼主受付（仮/正式）。trips とは分離
}

/**
 * @typedef {Object} Trip 運行（日計）: 1運行=1ドキュメント
 * @property {string} manageId  管理ID
 * @property {string} date      運行日 (YYYY-MM-DD)
 * @property {string} customerId 得意先
 * @property {string} staff     担当
 * @property {string} driverId  乗務員
 * @property {string} [partnerId] 協力会社（傭車時）
 * @property {'own'|'charter'} kind 区分（own=自社便 / charter=利用運送・傭車）
 * @property {string} vehicleId 車両 / 車番
 * @property {string} origin    発地
 * @property {string} dest      着地
 * @property {string} [item]    品名
 * @property {number} fare      運賃
 * @property {number} [charterCost] 傭車費
 * @property {number} [tollCost] 高速代
 * @property {number} [runKm]   実走行km
 * @property {number} [loadedKm] 実車km
 * @property {number} [emptyKm] 空車km
 * @property {number} [fuelCost] 実燃料費
 * @property {number} [grossProfit] 粗利 / 貢献利益
 *
 * --- 取込メタ（中身/TMS）。CSV取込の①重複除外・②変更検知に使う。出所: ingest/vendors.js ---
 * @property {'daily'|'trancom'|'hacobell'|string} [source] 取込元（配車日計/トランコム/ハコベル…）
 * @property {string} [sourceKey] ベンダー一意キー（案件番号/管理ID/複合キー）。①重複判定の素
 * @property {string} [_sig] signature（②比較用に可変項目を畳んだ文字列）
 * @property {string} [ankenNo] ハコベル案件番号（備考②/M列）。突合キー兼メモ
 * @property {string} [customerName] 取込時の得意先表記（マスタ突合前）。→ customerId は後段で解決
 * @property {string} [partnerName] 取込時の協力会社表記 @property {string} [driverName] 取込時の乗務員表記
 * @property {string} [unloadDate] 卸日 @property {string} [vehicleClass] 車種区分(4tw/10tw…)
 * @property {string} [memo] 備考② @property {string} [tel] 連絡先 @property {string} [shipper] 荷主
 * @property {number} [tollBill] 請通行料 @property {number} [tollPay] 支通行料 @property {boolean} [hasToll] 高速あり
 * @property {string} [month] 運行月(YYYY-MM) @property {string} [billMonth] 請求計上月
 * @property {string} [status] 案件ステータス @property {string} [billStatus] 請求ステータス @property {string} [payStatus] 支払ステータス
 * @property {boolean} [provisional] 「仮」運賃など未確定
 * @property {string} [importedAt] 初回取込 @property {string} [updatedAt] 最終更新 @property {string} [lastImportId] 直近ラン
 */

/**
 * @typedef {Object} ImportRun 取込ラン記録（中身/TMS）
 * @property {string} vendor @property {string} fileName @property {string} user @property {string} at
 * @property {Object} summary {total,new,changed,unchanged,error} @property {number} written @property {number} changed
 */

/**
 * @typedef {Object} TripChange 内容変更履歴（中身/TMS・②）
 * @property {string} tripId @property {string} vendor @property {string} at @property {string} runId
 * @property {Array<{field:string, from:*, to:*}>} diff 変更フィールドの前後
 */

/**
 * @typedef {Object} Customer 得意先
 * @property {string} code @property {string} name @property {string} [staff] 担当
 * @property {string} [address] @property {number} [unitPrice] 単価
 * @property {number} [branchNo] 枝番（相手先キー＝コード＋枝番） @property {string} [formal] 正式名称（御中表示）
 * @property {string} [tnum] 適格請求書登録番号(T) @property {string[]} [aliases] 日計表表記の別名
 */

/**
 * @typedef {Object} Partner 協力会社
 * @property {string} code @property {string} name @property {number} [charterRate] 傭車単価 @property {string} [contact]
 * @property {number} [branchNo] 枝番 @property {string} [formal] 正式名称 @property {string} [tnum] 登録番号(T) @property {string[]} [aliases]
 */

/**
 * @typedef {Object} Driver 乗務員
 * @property {string} code @property {string} name
 * @property {'commission'|'fixed'} wageType 給与区分（commission=歩合/運賃連動, fixed=日給月給/固定給）
 * @property {string} [staff] 担当 @property {string} [vehicleId]
 */

/**
 * @typedef {Object} Vehicle 車両
 * @property {string} plate 車番 @property {'large'|'medium'|string} klass 区分（大型/中型…）
 * @property {number} [capacity] 定員 @property {number} [maxLoad] 最大積載
 * @property {string} [inspectionDue] 車検 @property {string} [etc] @property {number} [dailyCost] 日額（車両経費）
 */

/**
 * @typedef {Object} Tariff 運賃タリフ
 * @property {string} block 運輸局ブロック @property {string} kmBand キロ程区分
 * @property {number} rate 単価 @property {string} effectiveFrom 適用日
 */

/**
 * @typedef {Object} Tachograph デジタコ実績（車番×日）
 * @property {string} plate 車番 @property {string} date 運行日
 * @property {number} runKm 走行km @property {number} loadedKm 実車km
 * @property {number} [fuelLiter] 給油量 @property {number} [fuelCost] 実燃料費
 *
 * --- デジタコ取込メタ（中身/TMS）。出所: ingest/vendors.js digitaco（118列・raw÷100=km） ---
 * @property {string} [source] 'digitaco' @property {string} [sourceKey] 運行ID等 @property {string} [_sig] ②比較用
 * @property {string} [tripId] 運行ID @property {string} [driverCode] 乗務員コード @property {string} [driverName]
 * @property {string} [vehicleNo] 車両番号 @property {string} [vehicleName] 車両名称
 * @property {number} [emptyKm] 空車km @property {string} [startAt] 運行開始 @property {string} [endAt] 運行終了
 * @property {number} [workSec] 稼働秒 @property {number} [driveSec] 運転秒 @property {string} [month] 運行月
 */

/**
 * @typedef {Object} ChargeSheet 運賃お伺い書 発行履歴（中身/TMS）
 * @property {string} targetYm 対象年月 @property {'seikyu'|'shiharai'} kind 請求/支払
 * @property {number} partyCode 相手先コード @property {number} [partyBranchNo] 枝番 @property {number} revision 版
 * @property {string} issueNo 発行番号 @property {string} status issued|sent|confirmed|settled|void
 * @property {number} amount 金額 @property {number} [tax] 消費税 @property {string} [tnum] 登録番号(T)
 */

/**
 * @typedef {Object} Invoice 請求
 * @property {string} customerId @property {string} period 対象期間
 * @property {number} amount 請求額 @property {'draft'|'preliminary'|'confirmed'} status 締めステータス
 */

/**
 * @typedef {Object} AppUser 利用者・ロール
 * @property {string} email @property {'admin'|'dispatch'|'office'} role @property {string} [staff] 担当
 */
